import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined"
import LinkIcon from "@mui/icons-material/Link"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import { Box, Typography, styled } from "@mui/material"
import type { AlertLevel, Category, Geotag } from "@phc/common"
import type React from "react"
import { Link } from "react-router"
import { dayjsUTC } from "../../../../utils/dates"
import { trackEvent } from "../../../../utils/mixpanel"
import { extraColors } from "../../../../utils/theme"
import { GeotagsContainer } from "../../../GeotagsContainer"
import type { JustificationConfidence } from "../../../Shared/AlertConfidence"
import { alertColors } from "../../../Shared/AlertPill"
import { ConfidenceIndicator } from "../../../Shared/ConfidenceIndicator"
import { ThreatCategory } from "../../../Shared/ThreatCategory"

export const CardContainer = styled("div")<{
  borderColor: string
  backgroundColor: string
}>(({ borderColor, backgroundColor }) => ({
  display: "flex",
  border: `1px solid ${borderColor}`,
  borderRadius: 6,
  backgroundColor,
  overflow: "hidden",
  height: "100%",
  maxWidth: 376,
}))

export const LeftBorder = styled("div")<{ color: string }>(
  ({ color }: { color: string }) => ({
    background: color,
    width: "24px",
  })
)

const ContentContainer = styled("div")({
  display: "flex",
  gap: 12,
  flexDirection: "column",
  width: "100%",
  padding: "12px",
})

const Row = styled("div")({
  display: "grid",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 15,
  gridTemplateColumns: "auto auto",
})

const LinkIconStyled = styled(LinkIcon)({
  fill: extraColors.white,
  height: "70%",
  marginRight: -10,
  cursor: "pointer",
  verticalAlign: "middle",
  "&:hover": {
    fill: extraColors.status.navyLight,
    transform: "scale(1.1)",
  },
})

const BorderText = styled(Typography)({
  position: "absolute",
  transform: "translate(-3%, 20%)",
  transformOrigin: "left top",
  writingMode: "vertical-rl",
  whiteSpace: "nowrap",
})

export interface IntelligenceCardProps {
  title: string

  date: string
  className?: string
  id: string
  threat: Category["title"]
  geotags: Geotag[] | undefined
  confidence?: JustificationConfidence
  alert_level?: AlertLevel
  sourceURL?: string
  slug?: string
}

export const IntelligenceCard = ({
  alert_level,
  title,
  date,
  className,
  sourceURL,
  id,
  threat,
  geotags,
  children,
  confidence,
  slug,
}: IntelligenceCardProps & { children: React.ReactNode }) => {
  const formattedDate = dayjsUTC(date).local().format("MM/D/YY")
  const colors = alert_level && alertColors[alert_level]

  return (
    <CardContainer
      className={className}
      borderColor={colors?.border || extraColors.light}
      backgroundColor={colors?.background || extraColors.white}
    >
      <LeftBorder color={colors?.sidebarBackground || extraColors.light} />
      <BorderText>
        {colors ? (
          <WarningAmberIcon
            sx={{
              color: colors.text,
              fontSize: "18px",
              marginBottom: "8px",
            }}
          />
        ) : (
          <ArticleOutlinedIcon
            sx={{
              color: extraColors.medium,
              fontSize: "18px",
              marginBottom: "8px",
            }}
          />
        )}
        <Typography
          variant="small1"
          color={colors?.text || extraColors.medium}
          sx={{ textTransform: "uppercase", fontWeight: 600 }}
        >
          {alert_level?.toLowerCase() ?? "Analysis"}
        </Typography>
      </BorderText>
      <ContentContainer>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "7px",
          }}
        >
          <ThreatCategory contentId={id} threat={threat} />
          <Typography variant="small1">{formattedDate}</Typography>
        </Box>
        <GeotagsContainer geotags={geotags} />
        <Row>
          <Typography
            variant="body2Bold"
            component={slug ? Link : "h4"}
            to={
              slug
                ? {
                    pathname: slug,
                  }
                : undefined
            }
          >
            {title}
          </Typography>
        </Row>
        <Box
          style={{
            position: "relative",
          }}
        >
          {children}
        </Box>
        {sourceURL && (
          <Box
            sx={{
              display: "flex",
              gap: "4px",
              justifyContent: "space-between",
              alignItems: "self-end",
              marginTop: "auto",
            }}
          >
            <Link
              to={sourceURL}
              target="_blank"
              onClick={() => {
                trackEvent("CLICK_ALERT_SOURCE", {
                  title,
                  link: sourceURL,
                })
              }}
              style={{
                display: "flex",
              }}
            >
              <Typography
                component="span"
                variant="tiny"
                sx={{ alignSelf: "center" }}
              >
                SOURCE
              </Typography>
              <LinkIconStyled fontSize="inherit" />
            </Link>
            {confidence && (
              <ConfidenceIndicator
                confidenceInteger={confidence.parsedConfidence}
              />
            )}
          </Box>
        )}
      </ContentContainer>
    </CardContainer>
  )
}
